// Transaction types for payment history
export interface Transaction {
  id: string;
  userId: string;
  citizenId: string;
  fullToolboxId: string;
  type: 'payment' | 'refund' | 'credit' | 'debit';
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  amount: number;
  currency: string;
  description: string;
  category: 'dues' | 'fees' | 'utilities' | 'maintenance' | 'other';

  // Related entities
  invoiceId?: string;
  paymentMethodId: string;
  paymentMethodType: 'bank' | 'card' | 'wallet' | 'crypto';

  // Payment details
  paymentProcessor?: string;
  transactionFee?: number;
  processingTime?: string;
  confirmationNumber?: string;

  // Metadata
  metadata?: {
    unit?: string;
    workOrder?: string;
    period?: string;
    [key: string]: string | number | boolean | object | undefined;
  };

  // Timestamps
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  failedAt?: string;
}

// Recent activity types
export interface RecentActivity {
  id: string;
  userId: string;
  type: 'payment' | 'invoice' | 'maintenance' | 'announcement' | 'governance';
  action: string;
  title: string;
  description: string;
  amount?: number;
  currency?: string;
  status?: string;
  relatedId?: string; // ID of related entity (invoice, transaction, etc.)
  createdAt: string;
  metadata?: {
    [key: string]: string | number | boolean | object | undefined;
  };
}

// Mock transaction data
export const transactions: Transaction[] = [
  {
    id: 'txn_001',
    userId: 'user_001',
    citizenId: '12345',
    fullToolboxId: 'Toolbox Type Citizen-12345',
    type: 'payment',
    status: 'completed',
    amount: 20.0,
    currency: 'USD',
    description: 'Guest Pass Fee - February 2024',
    category: 'fees',
    invoiceId: 'inv_fee_005',
    paymentMethodId: 'bank_001',
    paymentMethodType: 'bank',
    paymentProcessor: 'Stripe',
    transactionFee: 0.0,
    processingTime: '1-2 business days',
    confirmationNumber: 'TXN-2024-001-GP',
    metadata: {
      unit: '4A',
      passType: 'Monthly Guest Pass',
      period: 'February 2024',
    },
    createdAt: '2024-01-31T14:20:00Z',
    updatedAt: '2024-01-31T14:22:00Z',
    completedAt: '2024-01-31T14:22:00Z',
  },
  {
    id: 'txn_002',
    userId: 'user_001',
    citizenId: '12345',
    fullToolboxId: 'Toolbox Type Citizen-12345',
    type: 'payment',
    status: 'completed',
    amount: 450.0,
    currency: 'USD',
    description: 'March HOA Dues',
    category: 'dues',
    invoiceId: 'inv_hoa_002',
    paymentMethodId: 'card_001',
    paymentMethodType: 'card',
    paymentProcessor: 'Stripe',
    transactionFee: 13.05,
    processingTime: 'Instant',
    confirmationNumber: 'TXN-2024-002-HOA',
    metadata: {
      unit: '4A',
      period: 'March 2024',
      breakdown: {
        maintenance: 200.0,
        insurance: 125.0,
        reserves: 75.0,
        utilities: 50.0,
      },
    },
    createdAt: '2024-02-28T15:30:00Z',
    updatedAt: '2024-02-28T15:31:00Z',
    completedAt: '2024-02-28T15:31:00Z',
  },
  {
    id: 'txn_facility_001',
    userId: '12345',
    citizenId: '12345',
    fullToolboxId: 'Toolbox Type Citizen-12345',
    type: 'payment',
    status: 'completed',
    amount: 75.0,
    currency: 'USD',
    description: 'Facility Booking - Conference Room A',
    category: 'fees',
    invoiceId: 'inv_facility_book_001',
    paymentMethodId: 'wallet_001',
    paymentMethodType: 'wallet',
    paymentProcessor: 'Stripe',
    transactionFee: 0.0,
    processingTime: 'Instant',
    confirmationNumber: 'TXN-2024-003-FAC',
    metadata: {
      facilityId: 'facility-1',
      facilityName: 'Conference Room A',
      bookingId: 'book_1234567890_abc123',
      bookingDate: '2024-08-25',
      startTime: '14:00',
      endTime: '16:00',
      purpose: 'Team Planning Meeting',
      attendeeCount: '8',
      specialRequests: 'Need projector and whiteboard setup',
    },
    createdAt: '2024-08-15T10:30:00Z',
    updatedAt: '2024-08-15T10:32:00Z',
    completedAt: '2024-08-15T10:32:00Z',
  },
];

// Mock recent activity data
export const recentActivities: RecentActivity[] = [
  {
    id: 'activity_001',
    userId: 'user_001',
    type: 'payment',
    action: 'completed',
    title: 'Payment Successful',
    description: 'Paid Guest Pass Fee via Chase Checking',
    amount: 20.0,
    currency: 'USD',
    status: 'completed',
    relatedId: 'txn_001',
    createdAt: '2024-01-31T14:22:00Z',
    metadata: {
      paymentMethod: 'Chase Checking ****4567',
      confirmationNumber: 'TXN-2024-001-GP',
    },
  },
  {
    id: 'activity_002',
    userId: 'user_001',
    type: 'payment',
    action: 'completed',
    title: 'Payment Successful',
    description: 'Paid March HOA Dues via Visa Credit Card',
    amount: 450.0,
    currency: 'USD',
    status: 'completed',
    relatedId: 'txn_002',
    createdAt: '2024-02-28T15:31:00Z',
    metadata: {
      paymentMethod: 'Visa ****1234',
      confirmationNumber: 'TXN-2024-002-HOA',
    },
  },
  {
    id: 'activity_facility_001',
    userId: '12345',
    type: 'payment',
    action: 'completed',
    title: 'Facility Booking Payment',
    description: 'Paid Conference Room A booking via Digital Wallet',
    amount: 75.0,
    currency: 'USD',
    status: 'completed',
    relatedId: 'txn_facility_001',
    createdAt: '2024-08-15T10:32:00Z',
    metadata: {
      paymentMethod: 'Digital Wallet',
      confirmationNumber: 'TXN-2024-003-FAC',
      facilityName: 'Conference Room A',
      bookingDate: '2024-08-25',
    },
  },
  {
    id: 'activity_003',
    userId: 'user_001',
    type: 'invoice',
    action: 'received',
    title: 'New Invoice',
    description: 'February HOA Dues invoice received',
    amount: 450.0,
    currency: 'USD',
    status: 'sent',
    relatedId: 'inv_hoa_001',
    createdAt: '2024-01-15T00:00:00Z',
  },
  {
    id: 'activity_004',
    userId: 'user_001',
    type: 'maintenance',
    action: 'scheduled',
    title: 'Maintenance Scheduled',
    description: 'Annual HVAC inspection scheduled for Unit 4A',
    createdAt: '2024-02-20T10:00:00Z',
    metadata: {
      workOrder: 'WO-2024-0245',
      scheduledDate: '2024-03-15',
    },
  },
];

// Helper functions for transaction management
export const createTransaction = (
  invoiceId: string,
  amount: number,
  paymentMethodId: string,
  paymentMethodType: Transaction['paymentMethodType'],
  userId: string,
  citizenId: string,
  fullToolboxId: string,
  metadata?: Record<string, string | number | boolean | object | undefined>,
): Transaction => {
  const transactionId = `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const confirmationNumber = `TXN-${new Date().getFullYear()}-${String(transactions.length + 1).padStart(3, '0')}-${invoiceId.split('_')[1]?.toUpperCase()}`;

  return {
    id: transactionId,
    userId,
    citizenId,
    fullToolboxId,
    type: 'payment',
    status: 'pending',
    amount,
    currency: 'USD',
    description: typeof metadata?.description === 'string' ? metadata.description : 'Payment',
    category:
      typeof metadata?.category === 'string' &&
      ['dues', 'fees', 'utilities', 'maintenance', 'other'].includes(metadata.category)
        ? (metadata.category as Transaction['category'])
        : 'other',
    invoiceId,
    paymentMethodId,
    paymentMethodType,
    paymentProcessor: 'Stripe',
    transactionFee: paymentMethodType === 'card' ? amount * 0.029 : 0,
    processingTime: paymentMethodType === 'card' ? 'Instant' : '1-2 business days',
    confirmationNumber,
    metadata,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

export const completeTransaction = (transaction: Transaction): Transaction => {
  const now = new Date().toISOString();
  return {
    ...transaction,
    status: 'completed',
    updatedAt: now,
    completedAt: now,
  };
};

export const createRecentActivity = (
  transaction: Transaction,
  paymentMethodLabel: string,
): RecentActivity => {
  const activityId = `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return {
    id: activityId,
    userId: transaction.userId,
    type: 'payment',
    action: 'completed',
    title: 'Payment Successful',
    description: `Paid ${transaction.description} via ${paymentMethodLabel}`,
    amount: transaction.amount,
    currency: transaction.currency,
    status: 'completed',
    relatedId: transaction.id,
    createdAt: transaction.completedAt || transaction.createdAt,
    metadata: {
      paymentMethod: paymentMethodLabel,
      confirmationNumber: transaction.confirmationNumber,
    },
  };
};

export const getTransactionsByUser = (userId: string): Transaction[] => {
  return transactions.filter((txn) => txn.userId === userId || txn.citizenId === userId);
};

export const getRecentActivitiesByUser = (userId: string, limit: number = 10): RecentActivity[] => {
  return recentActivities
    .filter((activity) => activity.userId === userId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, limit);
};

export const formatTransactionAmount = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

export const getTransactionStatusColor = (status: Transaction['status']): string => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getActivityTypeIcon = (type: RecentActivity['type']): string => {
  switch (type) {
    case 'payment':
      return '💳';
    case 'invoice':
      return '📄';
    case 'maintenance':
      return '🔧';
    case 'announcement':
      return '📢';
    case 'governance':
      return '🗳️';
    default:
      return '📝';
  }
};
